import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";
import { useBrand } from "../../../../contexts/BrandContext";
import { useLoyaltySettings } from "../../hooks/useLoyaltySettings";
import { formatMoney, isBaseTier as isBaseLoyaltyTier, isPurchasesMode } from "../../utils/loyaltyQualification";

const TiersTab = ({
    tiersApi,        // { data, loading, error, refetch }
    pages,           // pages[0]
    handlePageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    const { currentBrand } = useBrand();
    const { qualificationMode } = useLoyaltySettings(currentBrand?.name || "");
    const purchases = isPurchasesMode(qualificationMode);
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [sortBy, setSortBy] = useState("rank_asc");

    const sourceData = Array.isArray(tiersApi.data) ? tiersApi.data : [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = [...sourceData]
            .filter((t) => {
                if (activeFilter === "active" && !t.active) return false;
                if (activeFilter === "inactive" && t.active) return false;
                if (!q) return true;
                return String(t.name || "").toLowerCase().includes(q)
                    || String(t.key || "").toLowerCase().includes(q);
            });
        rows.sort((a, b) => {
            if (sortBy === "name_asc") return String(a.name || "").localeCompare(String(b.name || ""), "fr");
            if (sortBy === "name_desc") return String(b.name || "").localeCompare(String(a.name || ""), "fr");
            if (sortBy === "points_desc") return Number(b.min_status_points || 0) - Number(a.min_status_points || 0);
            return Number(a.rank || 0) - Number(b.rank || 0);
        });
        return rows;
    }, [sourceData, search, activeFilter, sortBy]);

    if (tiersApi.error) return <ErrorState error={tiersApi.error} onRetry={() => tiersApi.refetch()} />;
    if (tiersApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (sourceData.length === 0) {
        return <EmptyState message="Aucun niveau de fidélité configuré" color={SECTION_COLORS.tiers.main} />;
    }

    const pageRaw = pages[0] || 0;
    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const page = Math.min(pageRaw, maxPage);
    const paginated = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
    const isBaseTier = (tier) => isBaseLoyaltyTier(tier);

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher un niveau (nom, key)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "rank_asc", label: "Rang (croissant)" },
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "name_desc", label: "Nom (Z-A)" },
                    { value: "points_desc", label: "Points min (décroissant)" },
                ]}
            />
            <Grid container spacing={2}>
                {paginated.map((tier) => (
                    <Grid item xs={12} sm={6} md={4} key={tier.id}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.tiers.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.tiers.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.tiers.main}30`,
                                },
                            }}
                        >
                            {/* Rank badge */}
                            <Tooltip title="Rang" placement="left">
                                <Box sx={{
                                    position: "absolute", top: 8, right: 8,
                                    width: 28, height: 28, borderRadius: "50%",
                                    background: SECTION_COLORS.tiers.gradient,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: `0 2px 8px ${SECTION_COLORS.tiers.main}40`, cursor: "default",
                                }}>
                                    <Typography fontSize={12} fontWeight={800} color="#fff">{tier.rank}</Typography>
                                </Box>
                            </Tooltip>

                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                <EmojiEventsIcon sx={{ color: SECTION_COLORS.tiers.main, fontSize: 22 }} />
                                <Typography fontWeight={700} fontSize={16} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                                    {tier.name}
                                </Typography>
                            </Box>
                            <Typography
                                fontSize={12}
                                color="text.secondary"
                                mb={0.5}
                                sx={{ fontWeight: purchases ? 400 : 700 }}
                            >
                                Points min. : <strong>{tier.min_status_points?.toLocaleString("fr-FR")}</strong>
                            </Typography>
                            <Typography
                                fontSize={12}
                                color="text.secondary"
                                mb={1.5}
                                sx={{ fontWeight: purchases ? 700 : 400 }}
                            >
                                Achats : <strong>{formatMoney(tier.min_purchase_count ?? 0)}</strong>
                                {" · "}
                                Montant : <strong>{formatMoney(tier.min_purchase_amount ?? 0)}</strong>
                            </Typography>

                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <StatusChip active={tier.active} />
                                <Box display="flex" gap={0.5}>
                                    <Tooltip title="Voir le détail">
                                        <IconButton size="small" onClick={() => onView(tier)}
                                            sx={{
                                                color: SECTION_COLORS.tiers.main, bgcolor: `${SECTION_COLORS.tiers.main}10`,
                                                "&:hover": { bgcolor: `${SECTION_COLORS.tiers.main}20` }, width: 30, height: 30
                                            }}>
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Modifier">
                                        <IconButton size="small" onClick={() => onEdit(tier)}
                                            sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={isBaseTier(tier) ? "Niveau de base — non supprimable" : "Supprimer"}>
                                        <span>
                                            <IconButton size="small"
                                                onClick={() => !isBaseTier(tier) && onDelete(tier)}
                                                disabled={isBaseTier(tier)}
                                                sx={{
                                                    color: isBaseTier(tier) ? "#9ca3af" : "#DC2626",
                                                    bgcolor: isBaseTier(tier) ? "#f3f4f6" : "#fef2f2",
                                                    "&:hover": { bgcolor: isBaseTier(tier) ? "#f3f4f6" : "#fee2e2" },
                                                    width: 30, height: 30,
                                                }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={filtered.length} page={page}
                    onPageChange={handlePageChange(0)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default TiersTab;
